console.log('[lp-quiz] script loaded');
const QUIZ_VERSION = 1;

// Campaign codes outside the LONGEVITY<n> family, whose percent can't be read off the name.
// Must stay in sync with CAMPAIGN_DISCOUNTS in longevity_plan_results.js — the quiz only decides
// which code to carry forward; the results page is what turns it into a percent and a checkout code.
const CAMPAIGN_DISCOUNTS = {
  FIMDOMES25: 25,
  PROMO25: 25,
};

function normaliseCode(code) {
  return (code || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
}

// 0 for anything this flow doesn't recognise, so an unknown code never displaces a real one.
function discountPercentOf(code) {
  const campaign = CAMPAIGN_DISCOUNTS[normaliseCode(code)];
  if (campaign) return campaign;
  const match = /^LONGEVITY-?(\d+)$/.exec((code || '').toUpperCase());
  return match ? parseInt(match[1], 10) : 0;
}

const storage = {
  _key: 'kandyQuizAnswers',
  _ttlDays: 14,

  save(answers) {
    try {
      localStorage.setItem(this._key, JSON.stringify({
        value: { ...answers },
        expiry: Date.now() + this._ttlDays * 24 * 60 * 60 * 1000,
      }));
    } catch (e) {
      console.warn('[lp-quiz] localStorage write failed', e);
    }
  },

  complete(answers) {
    try {
      localStorage.setItem(this._key, JSON.stringify({
        value: { ...answers, isCompleted: true },
        expiry: Date.now() + this._ttlDays * 24 * 60 * 60 * 1000,
      }));
    } catch (e) {
      console.warn('[lp-quiz] localStorage write failed', e);
    }
  },

  load() {
    try {
      const raw = localStorage.getItem(this._key);
      if (!raw) return {};
      const item = JSON.parse(raw);
      if (Date.now() > item.expiry) { localStorage.removeItem(this._key); return {}; }
      return item.value || {};
    } catch (e) {
      return {};
    }
  },
};

const state = {
  step: 1,
  answers: storage.load(),
};

let forcedFood = null; // 'fresh' | 'kibble' | null

// Birthdate is asked only for kibble puppies. It keeps id 11 (appended, not
// inserted) so none of the existing step numbers shift; STEP_ORDER is what puts
// it in the right place, right after the age question.
const BIRTHDATE_STEP = 11;

// The order steps are shown in. Navigation walks this list rather than doing
// arithmetic on step numbers, so a conditional step can sit anywhere.
const STEP_ORDER = [1, 2, 3, 4, BIRTHDATE_STEP, 5, 6, 7, 8, 9, 10];

function isStepActive(num) {
  // Food type came in via ?food=, so the question is skipped.
  if (num === 3 && forcedFood) return false;
  // Only the kibble range changes recipe as a puppy grows, so the exact date is
  // only worth asking for there.
  if (num === BIRTHDATE_STEP) {
    return state.answers.age === 'puppy' && state.answers.foodType === 'kibble';
  }
  return true;
}

function activeSteps() {
  return STEP_ORDER.filter(isStepActive);
}

/**
 * Walks STEP_ORDER — not the filtered list — so it still works when the current
 * step has become inactive (an answer changed, or a step number restored from an
 * older session).
 */
function stepFrom(num, direction) {
  const pos = STEP_ORDER.indexOf(num);
  if (pos === -1) return undefined;

  for (let i = pos + direction; i >= 0 && i < STEP_ORDER.length; i += direction) {
    if (isStepActive(STEP_ORDER[i])) return STEP_ORDER[i];
  }
  return undefined;
}

function displayStep(num) {
  const index = activeSteps().indexOf(num);
  return index === -1 ? num : index + 1;
}

function totalSteps() {
  return activeSteps().length;
}

const FRESH_STEP_5 = {
  id: 5,
  type: 'multi_choice',
  question: '{O_name} tem alguma alergia?',
  key: 'allergies',
  options: [
    { label: 'Nenhuma', value: 'none' },
    { label: 'Frango', value: 'chicken' },
    { label: 'Salmão', value: 'salmon' },
    { label: 'Carne de vaca', value: 'beef' },
    { label: 'Glúten', value: 'gluten' },
    { label: 'Leveduras', value: 'yeast' },
    { label: 'Outra', value: 'other' },
  ],
  fact: "Apostamos na transparência na composição da nossa ração. O que selecionar aqui ficará totalmente fora do plano do seu cão. Sem exceções.",
};

const KIBBLE_STEP_5 = {
  id: 5,
  type: 'single_choice',
  question: 'Que ração come habitualmente {o_name}?',
  key: 'currentRecipe',
  columns: 3,
  options: [
    { label: 'Carne', value: 'meat' },
    { label: 'Peixe', value: 'fish' },
    { label: 'Hipoalérgénica', value: 'hypoallergenic' },
  ],
  fact: 'Selecionamos a ração ideal com base nos hábitos alimentares do seu cão, para garantir uma transição suave e sem surpresas.',
};

// Shown only for a kibble puppy — see isStepActive.
const BIRTHDATE_STEP_DEF = {
  id: BIRTHDATE_STEP,
  type: 'birthdate_input',
  question: 'Quando nasceu {o_name}?',
  key: 'birthdate',
  skipLabel: 'Não sei a data de nascimento',
  fact: 'Nos primeiros meses, as necessidades mudam praticamente semana a semana. Com a data de nascimento {do_name}, ajustamos a receita para se adaptar ao seu crescimento.',
};

const steps = [
  {
    id: 1,
    type: 'text_input',
    question: "Como se chama o seu cão?",
    placeholder: "O nome do seu cão",
    key: 'name',
    fact: "Os cães conseguem aprender mais de 150 palavras! Estamos ansiosos para que o seu cão aprenda uma nova: Barkyn!",
  },
  {
    id: 2,
    type: 'single_choice_group',
    groups: [
      {
        question: '{name} é macho ou fêmea?',
        isMain: true,
        key: 'gender',
        options: [
          { label: 'Macho', value: 'male' },
          { label: 'Fêmea', value: 'female' },
        ],
      },
      {
        question_male: 'O {name} está castrado?',
        question_female: 'A {name} está esterilizada?',
        key: 'neutered',
        options: [
          { label: 'Sim', value: 'yes' },
          { label: 'Não', value: 'no' },
        ],
      },
    ],
    fact: 'Após a esterilização, o metabolismo abranda e os cães precisam de menos 20% de calorias. Na Barkyn, ajustamos a dose da nossa receita para manter o peso ideal, sem passar fome!',
  },
  {
    id: 3,
    type: 'food_type_choice',
    question: 'Qual a principal forma de alimentar {o_name}?',
    key: 'foodType',
    options: [
      { label: 'Ração', value: 'kibble', subtitle: 'Outra marca de ração', imageKey: 'kibble' },
      { label: 'Comida fresca', value: 'fresh', subtitle: 'Caseira ou marca de comida fresca', imageKey: 'fresh' },
    ],
    fact: 'Cada cão é único. Escolha o tipo de alimentação que melhor se adapta ao seu estilo de vida e às necessidades do seu cão.',
  },
  {
    id: 4,
    type: 'single_choice',
    question: 'Quantos anos tem {o_name}?',
    key: 'age',
    columns: 3,
    options: [
      { label: 'Menos de 1 ano', value: 'puppy' },
      { label: 'De 1 a 7 anos', value: 'adult' },
      { label: '8 anos ou mais', value: 'senior' },
    ],
    fact: 'As necessidades do seu cão mudam com a idade. Calculamos a energia exata necessária para a sua fase de vida atual através do nosso algoritmo.',
  },
  {
    id: 6,
    type: 'number_input',
    question: "Quanto pesa {o_name}?",
    placeholder: '0',
    unit: 'Kg',
    key: 'weight',
    fact: "Em Portugal, 40% dos cães sofrem de excesso de peso. Por isso, calculamos a dose exata para o seu cão e monitorizamos a sua evolução para que se mantenha sempre no peso ideal.",
  },
  {
    id: 7,
    type: 'single_choice',
    question: 'Qual é o nível de atividade {do_name}?',
    key: 'activity',
    columns: 3,
    options: [
      { label: 'Vida tranquila', value: 'low', subtitle: '1h por dia' },
      { label: 'Ritmo ativo', value: 'moderate', subtitle: '1h a 2h por dia' },
      { label: 'Pura energia!', value: 'high', subtitle: 'Mais de 2h por dia' },
    ],
    fact: 'Adaptamos a nutrição à atividade real do seu cão. Além disso, pode monitorizar os seus passeios com a nossa App gratuita.',
  },
  {
    id: 8,
    type: 'single_choice',
    question: 'À mesa, qual é o nível de exigência {do_name}?',
    key: 'appetite',
    columns: 3,
    options: [
      { label: 'Come de tudo', value: 'enthusiastic' },
      { label: 'Um pouco exigente', value: 'depends' },
      { label: 'Muito seletivo', value: 'picky' },
    ],
    fact: "As nossas receitas são formuladas para garantir a máxima palatabilidade. Através de um equilíbrio perfeito de ingredientes naturais, criamos um sabor irresistível, até para os cães mais exigentes.",
  },
  {
    id: 9,
    type: 'single_choice',
    question: 'O que gostava de melhorar no seu cão?',
    key: 'goal',
    columns: 2,
    options: [
      { label: 'Bem-estar geral', value: 'wellbeing' },
      { label: 'Pelo e pele', value: 'skin_coat' },
      { label: 'Articulações', value: 'mobility' },
      { label: 'Peso', value: 'control_weight' },
      { label: 'Digestão', value: 'digestion' },
    ],
    fact: "Além das receitas, terá sempre a nossa equipa veterinária à distância de um clique para ajustar o plano do seu cão conforme a sua evolução e ter um acompanhamento constante.",
  },
];

const EMAIL_STEP = {
  id: 10,
  type: 'email_input',
  question: 'Desbloqueie o plano {do_name}',
  lede: 'Deixe o seu email e veja já o plano completo {do_name}: a dose diária, a receita e o acompanhamento veterinário incluído.',
  // Stands for the plan behind the email. Blurred and hidden from screen readers, so the labels
  // are generic on purpose — they must not read as a promise of any particular value.
  lockedTitle: 'O plano {do_name} está pronto',
  lockedRows: [
    { label: 'Dose diária', value: '000 g' },
    { label: 'Receita', value: 'Frango' },
    { label: 'Acompanhamento', value: 'Incluído' },
  ],
  trust: ['Formulado por veterinários', 'Sem compromisso'],
  fieldLabel: 'O seu email',
  placeholder: 'o.seu@email.com',
  key: 'email',
  ctaLabel: 'Ver o plano {do_name}',
};

/**
 * A chicken-allergic dog never reaches the results page (see showChickenBlock), so this step must
 * not promise a plan — and least of all a locked card whose recipe row reads "Frango". It must not
 * say we have nothing for them either: that conversation belongs to the vet, on the next screen.
 *
 * So it stays neutral and deliberately plain — the step as it was before the plan version, asking
 * for the email and nothing more. Every optional field is left out, which is what makes the
 * builder render the simple layout.
 */
const EMAIL_STEP_ALLERGY = {
  id: 10,
  type: 'email_input',
  question: 'Último passo. Qual é o seu email?',
  placeholder: 'o.seu@email.com',
  key: 'email',
  fact: 'Sem spam, só o essencial.',
};

function getStage() {
  return document.getElementById('lp-quiz-stage');
}

function stepSlug(step) {
  return step.key || (step.groups && step.groups[0].key) || `step${step.id}`;
}

const MONTH_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function getStep(num) {
  if (num === BIRTHDATE_STEP) return BIRTHDATE_STEP_DEF;
  if (num === 10) return hasBlockingAllergy(state.answers) ? EMAIL_STEP_ALLERGY : EMAIL_STEP;
  if (num === 5) {
    if (state.answers.foodType !== 'kibble') return FRESH_STEP_5;
    // No hypoallergenic recipe for puppies — hide that option entirely.
    if (state.answers.age === 'puppy') {
      return {
        ...KIBBLE_STEP_5,
        columns: 2,
        options: KIBBLE_STEP_5.options.filter(opt => opt.value !== 'hypoallergenic'),
      };
    }
    return KIBBLE_STEP_5;
  }
  const idx = num <= 4 ? num - 1 : num - 2;
  return steps[idx];
}

function renderStep(num) {
  const step = getStep(num);
  if (!step) return;

  const url = new URL(window.location.href);
  url.searchParams.set('question', stepSlug(step));
  history.replaceState(null, '', url.toString());

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: url.pathname + url.search,
    quiz_step: num,
    quiz_step_name: stepSlug(step),
  });

  const stage = getStage();

  if (step.type === 'text_input') {
    stage.innerHTML = buildTextInput(step, num);
    initTextInput(step);
  } else if (step.type === 'single_choice_group') {
    stage.innerHTML = buildSingleChoiceGroup(step, num);
    initSingleChoiceGroup(step);
  } else if (step.type === 'multi_choice') {
    stage.innerHTML = buildMultiChoice(step, num);
    initMultiChoice(step);
  } else if (step.type === 'number_input') {
    stage.innerHTML = buildNumberInput(step, num);
    initNumberInput(step);
  } else if (step.type === 'food_type_choice') {
    stage.innerHTML = buildFoodTypeChoice(step, num);
    initFoodTypeChoice(step);
  } else if (step.type === 'single_choice') {
    stage.innerHTML = buildSingleChoice(step, num);
    initSingleChoice(step);
  } else if (step.type === 'email_input') {
    stage.innerHTML = buildEmailInput(step, num);
    initEmailInput(step);
  } else if (step.type === 'birthdate_input') {
    stage.innerHTML = buildBirthdateInput(step, num);
    initBirthdateInput(step);
  }
}

function interpolate(text) {
  const rawName = state.answers.name;
  const isFemale = state.answers.gender === 'female';
  const name = escapeHtml(rawName || 'seu cão');
  const oA = isFemale ? 'a' : 'o';
  const OA = isFemale ? 'A' : 'O';
  const doDA = isFemale ? 'da' : 'do';
  return text
    .replace(/{O_name}/g, rawName ? `${OA} ${name}` : 'O seu cão')
    .replace(/{o_name}/g, rawName ? `${oA} ${name}` : 'o seu cão')
    .replace(/{do_name}/g, rawName ? `${doDA} ${name}` : 'do seu cão')
    .replace(/{name}/g, name);
}

function buildTextInput(step, num) {
  const saved = escapeHtml(state.answers[step.key] || '');
  const hasValue = saved.length > 0;
  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${step.question}</h1>
          <input
            type="text"
            class="lp-quiz__text-input"
            id="lp-input"
            placeholder="${step.placeholder}"
            value="${saved}"
            autocomplete="off"
          >
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <div class="lp-quiz__back-placeholder"></div>
              <button
                class="lp-quiz__next-btn"
                id="lp-next"
                type="button"
                ${hasValue ? '' : 'disabled'}
              >Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initTextInput(step) {
  const input = document.getElementById('lp-input');
  const next = document.getElementById('lp-next');

  syncFooterSticky(!next.disabled);

  input.addEventListener('input', () => {
    next.disabled = input.value.trim() === '';
    syncFooterSticky(!next.disabled);
  });

  const submitText = () => {
    const val = input.value.trim();
    if (!val) return;
    state.answers[step.key] = val;
    storage.save(state.answers);
    advance();
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitText();
  });

  next.addEventListener('click', submitText);

  input.focus();
}

// The one allergy the fresh recipe cannot work around: it is chicken-based. Everything else the
// dog is allergic to is simply left out of the plan.
const BLOCKING_ALLERGY = 'chicken';

// Its own metric, so the Klaviyo flows for these leads stay separate from the ones that push a
// plan. Create the flow in Klaviyo under this exact name.
const ALLERGY_METRIC = 'LP Quiz Completed - Allergy';

// Only the fresh path asks about allergies (see getStep), and a stale `allergies` answer can
// survive a switch to kibble in localStorage — so the food type is part of the test.
function hasBlockingAllergy(answers) {
  return answers.foodType !== 'kibble' && (answers.allergies || []).includes(BLOCKING_ALLERGY);
}

// The two destinations a plan link can have. Both carry the identical ?qa= payload: the results
// page renders the plan and waits for a click, the checkout page adds to cart on load and goes
// straight to checkout. Keep them building from one function so a new quiz field reaches both.
const RESULTS_PATH = '/pages/longevity-plan-quiz-results';
const CHECKOUT_PATH = '/pages/longevity-plan-checkout';

function buildPlanLink(path, answers, discountOverride) {
  const isKibble = answers.foodType === 'kibble';
  const discountCode = discountOverride || (isKibble ? '' : (answers.discountCode || localStorage.getItem('longevityDiscountCode') || ''));
  const payload = {
    v: QUIZ_VERSION,
    name: answers.name || '',
    gender: answers.gender || '',
    neutered: answers.neutered || '',
    allergies: answers.allergies || [],
    weight: answers.weight || '',
    age: answers.age || '',
    birthdate: answers.birthdate || '',
    isBirthdateUnknown: !!answers.isBirthdateUnknown,
    activity: answers.activity || '',
    appetite: answers.appetite || '',
    goal: answers.goal || '',
    email: answers.email || '',
    foodType: answers.foodType || 'fresh',
    currentRecipe: answers.currentRecipe || '',
    isCompleted: true,
    discountCode,
    // WEB-334: the plan link keeps the quiz's id, so a purchase made from it links to the lead.
    ...(answers.submissionId ? { submissionId: answers.submissionId } : {}),
  };
  const encoded = btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(payload))));
  const url = new URL(path, window.location.origin);
  url.searchParams.set('qa', encoded);
  if (discountCode) url.searchParams.set('discount', discountCode);
  return url.toString();
}

function buildResultsLink(answers, discountOverride) {
  return buildPlanLink(RESULTS_PATH, answers, discountOverride);
}

function buildCheckoutLink(answers, discountOverride) {
  return buildPlanLink(CHECKOUT_PATH, answers, discountOverride);
}

// The link properties the profile carries: a plan link and a direct-to-checkout link at each
// discount tier the escalation flows offer, so a flow can swap one for the other on its own.
function buildLinkProps(answers) {
  return {
    lp_results_url: buildResultsLink(answers),
    lp_results_url_10: buildResultsLink(answers, 'LONGEVITY10'),
    lp_results_url_20: buildResultsLink(answers, 'LONGEVITY20'),
    lp_results_url_30: buildResultsLink(answers, 'LONGEVITY30'),
    lp_checkout_url: buildCheckoutLink(answers),
    lp_checkout_url_10: buildCheckoutLink(answers, 'LONGEVITY10'),
    lp_checkout_url_20: buildCheckoutLink(answers, 'LONGEVITY20'),
    lp_checkout_url_30: buildCheckoutLink(answers, 'LONGEVITY30'),
  };
}

const QUIZ_NAME = 'longevity-plan';

function newSubmissionId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function lastUtm() {
  let last = {};
  try { last = JSON.parse(localStorage.getItem('bk_utm_last') || 'null') || {}; } catch (e) {}
  const utm = {};
  ['source', 'medium', 'campaign', 'term', 'content'].forEach(key => {
    if (last[key]) utm[`utm_${key}`] = String(last[key]).slice(0, 200);
  });
  return utm;
}

function getCoreApiUrl() {
  return /(^|\.)barkyn\.(com|es|it)$/.test(window.location.hostname)
    ? 'https://core-api.barkyn.com'
    : 'https://core-api-staging.barkyn.com';
}

// WEB-334: one id per completed quiz, kept in the stored answers so the plan links and the cart
// (`_dogs` additional_info) carry it and a purchase can be linked to the lead. Finishing the quiz
// again on a new visit gives a new id (and a new lead); going back and forth on one visit keeps it.
let visitSubmissionId = null;

function stampSubmissionId(answers) {
  if (!visitSubmissionId) visitSubmissionId = newSubmissionId();
  answers.submissionId = visitSubmissionId;
}

function saveQuizLead(answers) {
  const { email, submissionId, ...quizAnswers } = answers;
  try {
    fetch(`${getCoreApiUrl()}/quiz/leads`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        submissionId: submissionId || newSubmissionId(),
        email: email.trim(),
        quiz: QUIZ_NAME,
        quizVersion: QUIZ_VERSION,
        utm: lastUtm(),
        answers: quizAnswers,
      }),
    }).catch(() => {});
  } catch (e) {
    console.warn('[lp-quiz] quiz lead save failed', e);
  }
}

/**
 * Collects the lead. `allergyBlocked` leads are collected exactly like the others — same profile,
 * same list — but they get their own event metric and no results URLs, because the plan this quiz
 * would sell them contains the very thing their dog is allergic to.
 */
async function syncKlaviyo(answers, { allergyBlocked = false } = {}) {
  if (!answers.email) return;

  saveQuizLead(answers);

  const properties = {
    lp_dog_name: answers.name || '',
    lp_dog_gender: answers.gender || '',
    lp_dog_neutered: answers.neutered || '',
    lp_dog_allergies: (answers.allergies || []).filter(a => a !== 'none').join(', ') || 'none',
    lp_dog_weight_kg: answers.weight || '',
    lp_dog_age: answers.age || '',
    lp_dog_activity: answers.activity || '',
    lp_dog_appetite: answers.appetite || '',
    lp_dog_goal: answers.goal || '',
    lp_food_type: answers.foodType || 'fresh',
    lp_kibble_recipe: answers.currentRecipe || '',
    lp_quiz_completed_at: new Date().toISOString(),
    lp_discount_code: answers.discountCode || '',
    // Written on both paths so it can never go stale: a lead who goes back and unticks chicken
    // must stop looking like an allergy lead.
    lp_allergy_block: allergyBlocked ? BLOCKING_ALLERGY : '',
    // A blocked lead has no plan to link to, and both link targets build one from a ?qa= link
    // without asking — the checkout link would not even show it first — so the URLs are left off
    // the profile entirely.
    ...(allergyBlocked ? {} : buildLinkProps(answers)),
  };

  // Sent first and deliberately not awaited. The preloader redirects ~3.5s after this function
  // starts, and while this was the last of five sequential calls it was routinely cancelled
  // mid-flight — which is how a profile ends up carrying every lp_* property and no event to
  // trigger the conversion flow. `keepalive` lets it outlive the navigation.
  // It does NOT record consent — createKlaviyoProfile below is what does that — so the two start
  // together rather than this one replacing it.
  try {
    fetch('https://a.klaviyo.com/client/events/?company_id=TN8fCF', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties,
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name: allergyBlocked
                    ? ALLERGY_METRIC
                    : (answers.foodType === 'kibble' ? 'LP Quiz Completed - Kibble' : 'LP Quiz Completed - Fresh'),
                },
              },
            },
            // Piggyback the full profile properties on the event: /client/events upserts the
            // profile atomically, so the lp_* props land even for brand-new users (the separate
            // updateKlaviyoProfile call no-ops when it runs before the profile exists).
            profile: {
              data: { type: 'profile', attributes: { email: answers.email, properties } },
            },
          },
        },
      }),
    }).catch(() => {});
  } catch (e) {
    console.warn('[lp-quiz] klaviyo-event failed', e);
  }

  // Moved up from the tail of the function, where it sat behind three awaits and was the other
  // call the redirect used to cancel — which is how a lead ends up with properties and no list
  // subscription. Same treatment as the event: fired here, keepalive, never awaited.
  try {
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=TN8fCF', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: answers.email } } },
          },
          relationships: {
            list: { data: { type: 'list', id: 'TPrz5K' } },
          },
        },
      }),
    }).catch(() => {});
  } catch (e) {
    console.warn('[lp-quiz] klaviyo-subscribe failed', e);
  }

  // The profile write is the one pair that has to stay in order: updateKlaviyoProfile is a PATCH
  // by email that silently no-ops when the profile does not exist yet, so it is chained off the
  // create instead of firing beside it. The chain itself is NOT awaited — nothing below depends
  // on it, and while it was, everything after it waited on two round trips to the middleware and
  // got cancelled by the redirect.
  if (typeof window.createKlaviyoProfile === 'function') {
    Promise.resolve(window.createKlaviyoProfile({
      email: answers.email,
      locale: 'pt-PT',
      location: { region: 'PT' },
      properties: { $source: 'Longevity Plan Quiz', 'Accepts Marketing': true, Account_to_claim: true, ...properties },
    }))
      // Settle, do not short-circuit. The update used to sit in its own try/catch, so it ran even
      // when the create failed — and for a profile that already exists the PATCH still lands.
      // Chaining it off a rejected create would have silently dropped that case.
      .catch(e => console.warn('[lp-quiz] Klaviyo create failed', e))
      .then(() => {
        if (typeof window.updateKlaviyoProfile === 'function') {
          return window.updateKlaviyoProfile(answers.email, properties);
        }
      })
      .catch(e => console.warn('[lp-quiz] Klaviyo update failed', e));
  } else if (typeof window.updateKlaviyoProfile === 'function') {
    // No create helper loaded: the PATCH is all there is, and it only lands for a profile that
    // already exists. The event above is what covers a brand-new lead.
    Promise.resolve(window.updateKlaviyoProfile(answers.email, properties))
      .catch(e => console.warn('[lp-quiz] Klaviyo update failed', e));
  }

  try {
    const authApiUrl = document.querySelector('.barkyn-auth-api-url')?.value
      || 'https://8j0t014re0.execute-api.eu-west-3.amazonaws.com/dev';
    fetch(`${authApiUrl}/klaviyo-register`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: answers.email, claimedAccount: true, isLongevity: true }),
    }).catch(() => {});
  } catch (e) {
    console.warn('[lp-quiz] Klaviyo register failed', e);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'email_submission',
    Email: answers.email,
    Store: window.location.origin,
    quiz_outcome: allergyBlocked ? 'allergy' : 'plan',
  });
  if (answers.email) {
    document.cookie = 'bk_email=' + encodeURIComponent(answers.email) + '; path=/; max-age=2592000; SameSite=Lax';
  }

  (function () {
    var GA4_ID = 'G-M5990PECK8';
    var SERVER_URL = 'https://cdpgcp.barkyn.com';
    if (!window.gtag) {
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_ID;
      document.head.appendChild(s);
      window.gtag = function () { dataLayer.push(arguments); };
      gtag('js', new Date());
      gtag('config', GA4_ID, { transport_url: SERVER_URL, first_party_collection: true, send_page_view: false });
    }
    var bkFirst = {}; var bkLast = {}; var bkCoupon = '';
    try { bkFirst = JSON.parse(localStorage.getItem('bk_utm_first') || 'null') || {}; } catch (e) {}
    try { bkLast = JSON.parse(localStorage.getItem('bk_utm_last') || 'null') || {}; } catch (e) {}
    try { bkCoupon = JSON.parse(localStorage.getItem('bk_coupon') || 'null') || ''; } catch (e) {}
    var payload = {
      email: answers.email,
      acquisition_first_source: bkFirst.source || '',
      acquisition_first_medium: bkFirst.medium || '',
      acquisition_first_campaign: bkFirst.campaign || '',
      acquisition_first_content: bkFirst.content || '',
      acquisition_first_term: bkFirst.term || '',
      acquisition_last_source: bkLast.source || '',
      acquisition_last_medium: bkLast.medium || '',
      acquisition_last_campaign: bkLast.campaign || '',
      acquisition_last_content: bkLast.content || '',
      acquisition_last_term: bkLast.term || '',
      first_touch_match_type: bkFirst.match_type || 'direct',
      coupon_applied: bkCoupon,
    };
    gtag('set', 'user_properties', payload);
    gtag('event', 'lead', payload);
  })();


}

function advance() {
  const next = stepFrom(state.step, 1);
  if (next === undefined) {
    showPreloader();
    return;
  }
  state.step = next;
  renderStep(state.step);
}

const whatsappIconSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>`;

/**
 * The vet hand-off, shown after the loading for a chicken-allergic dog. The lead was already
 * collected in showPreloader, so this only renders.
 */
function showChickenBlock() {
  const url = new URL(window.location.href);
  url.searchParams.set('question', 'chicken-allergy');
  history.replaceState(null, '', url.toString());

  // Every step reports itself as a page_view; this screen is the end of the funnel for these
  // leads, so it reports one too.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: url.pathname + url.search,
    quiz_step_name: 'chicken-allergy',
  });

  const rawName = state.answers.name;
  const isFemaleChicken = state.answers.gender === 'female';
  const oName = rawName ? escapeHtml((isFemaleChicken ? 'a ' : 'o ') + rawName) : 'o seu cão';
  const doName = rawName ? escapeHtml((isFemaleChicken ? 'da ' : 'do ') + rawName) : 'do seu cão';
  const vetBtn = `<button class="lp-quiz__whatsapp-btn" type="button">${whatsappIconSvg} Falar com um veterinário</button>`;
  const stage = getStage();
  stage.innerHTML = `
    <div class="lp-quiz__step">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left"></div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">Queremos fazer isto bem para ${oName}</h1>
          <p class="lp-quiz__block-desc">A nossa receita principal contém frango, por isso não conseguimos desenhar o plano ${doName} por este caminho. Um dos nossos veterinários vai ajudar-te a criar um plano totalmente adaptado — contacta-nos e nós tratamos de tudo.</p>
          <div class="lp-quiz__block-cta-desktop">${vetBtn}</div>
          <div class="lp-quiz__bottom">
            <div class="lp-quiz__footer lp-quiz__footer--sticky" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <div class="lp-quiz__block-cta-mobile">${vetBtn}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const handleVet = () => { window.open('https://wa.me/351913496251?text=O%20meu%20c%C3%A3o%20tem%20alergias.%20Quero%20ajuda%20de%20um%20veterin%C3%A1rio.%20%F0%9F%A5%BC', '_blank'); };
  document.getElementById('lp-back').addEventListener('click', () => renderStep(state.step));
  stage.querySelectorAll('.lp-quiz__whatsapp-btn').forEach(btn => btn.addEventListener('click', handleVet));
  syncFooterSticky(true);
}

// The back button on the vet screen can bring the same lead through here twice.
let allergyLeadSent = false;
// Same idea for the plan path: Enter and a click on the same tap both reach advance(), and the vet
// screen's back button leads here again. Kept as its own flag so a lead who goes back and unticks
// chicken still gets their fresh/kibble event.
let planLeadSent = false;

/**
 * Every dog sees this. The split is at the end of it: a plan on the results page, or the vet
 * hand-off for a dog allergic to chicken — so the wait feels the same either way.
 */
function showPreloader() {
  const allergyBlocked = hasBlockingAllergy(state.answers);
  stampSubmissionId(state.answers);

  if (allergyBlocked) {
    // Deliberately NOT storage.complete(): the results page bounces anyone whose answers are not
    // marked complete, which is exactly what should happen to a lead with no buyable plan.
    storage.save(state.answers);
    if (!allergyLeadSent) {
      allergyLeadSent = true;
      syncKlaviyo(state.answers, { allergyBlocked: true });
    }
  } else {
    storage.complete(state.answers);
    sessionStorage.setItem('lp_klaviyo_sync', JSON.stringify(state.answers));
    if (!planLeadSent) {
      planLeadSent = true;
      syncKlaviyo(state.answers);
    }
  }

  const phases = [
    {
      label: 'Fase 01',
      title: 'Analisando <em>necessidades nutricionais</em>',
      detail: 'A construir o plano base',
      color: '#7BA87C',
      phaseName: 'Base',
      iconBg: '#E1EED8',
      iconSvg: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7BA87C" stroke-width="2" stroke-linecap="round"><path d="M3 13c0 4 4 7 9 7s9-3 9-7"/><ellipse cx="12" cy="13" rx="9" ry="3"/><path d="M8 10c1-3 3-5 4-5s3 2 4 5"/></svg>`,
    },
    {
      label: 'Fase 02',
      title: 'Identificando <em>reforços específicos</em>',
      detail: 'A configurar os reforços',
      color: '#D4954A',
      phaseName: 'Reforço',
      iconBg: '#F5E6CE',
      iconSvg: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4954A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/></svg>`,
    },
    {
      label: 'Fase 03',
      title: 'Ativando <em>acompanhamento contínuo</em>',
      detail: 'A configurar o sistema de monitorização',
      color: '#7B61C1',
      phaseName: 'Acompanhamento',
      iconBg: '#E4DDEF',
      iconSvg: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7B61C1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
    },
  ];

  const overlay = document.createElement('div');
  overlay.className = 'lp-preloader';
  overlay.innerHTML = `
    <span class="lp-preloader__logo"><svg width="118" height="36" viewBox="0 0 118 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.0697219 2.39048C0.0697219 2.15874 0.15892 1.9365 0.317693 1.77264C0.476466 1.60878 0.691809 1.51672 0.916348 1.51672L7.71924 1.51672C15.2891 1.51672 18.6756 3.91184 18.6756 8.3937C18.6756 11.1589 17.1218 12.8858 14.6915 13.7802L14.6915 13.8521C18.058 14.8801 19.6716 17.1827 19.6716 20.4001C19.6716 25.4885 15.8668 28.6237 7.81884 28.6237L0.846627 28.6237C0.622087 28.6237 0.406745 28.5317 0.247971 28.3678C0.0891981 28.204 -3.76676e-08 27.9817 -3.73378e-08 27.75L-1.24361e-09 2.39048L0.0697219 2.39048ZM8.87463 12.67C11.2651 12.67 12.8588 11.7345 12.8588 9.58612C12.8588 7.43771 11.5938 6.50227 7.56984 6.50227L6.68337 6.50227C6.45883 6.50227 6.24348 6.59433 6.08471 6.75819C5.92594 6.92206 5.83674 7.14429 5.83674 7.37603L5.83674 11.7654C5.83674 11.9971 5.92594 12.2193 6.08471 12.3832C6.24348 12.5471 6.45883 12.6391 6.68337 12.6391L8.8846 12.6391L8.87463 12.67ZM7.96825 23.4429C12.7492 23.4429 13.9444 22.4663 13.9444 20.0815C13.9444 17.6966 12.2811 16.7818 9.29297 16.7818L6.68337 16.7818C6.45883 16.7818 6.24348 16.8738 6.08471 17.0377C5.92594 17.2015 5.83674 17.4238 5.83674 17.6555L5.83674 22.6C5.83674 22.8317 5.92594 23.0539 6.08471 23.2178C6.24348 23.3817 6.45883 23.4737 6.68337 23.4737L7.97821 23.4737L7.96825 23.4429Z" fill="#3A3535"/><path d="M24.5339 27.7841C23.8627 27.3071 23.3184 26.6636 22.9512 25.9128C22.5839 25.1619 22.4055 24.3279 22.4323 23.4872C22.408 22.6176 22.5937 21.7555 22.9728 20.9784C23.3519 20.2014 23.9123 19.5339 24.6037 19.0362C26.101 18.0997 27.8349 17.6452 29.5838 17.7307C30.6186 17.7287 31.6505 17.8425 32.6616 18.0699L32.6616 17.3607C32.6824 17.0055 32.6322 16.6497 32.514 16.3154C32.3957 15.9811 32.212 15.6756 31.9743 15.4178C31.1779 14.8267 30.2026 14.5495 29.2253 14.6366C27.834 14.6534 26.4597 14.9538 25.1814 15.5206C25.0529 15.5765 24.9131 15.5987 24.7743 15.5854C24.6355 15.5721 24.5021 15.5236 24.3858 15.4443C24.2695 15.365 24.174 15.2573 24.1078 15.1307C24.0415 15.0042 24.0066 14.8627 24.006 14.7188L24.006 11.7583C24.0059 11.5882 24.0538 11.4216 24.1441 11.2792C24.2343 11.1367 24.3629 11.0246 24.514 10.9565C26.3146 10.1617 28.2529 9.7525 30.2113 9.75384C32.3388 9.62465 34.4457 10.2444 36.1875 11.5116C36.8825 12.1395 37.4249 12.9271 37.7709 13.8109C38.1169 14.6947 38.2569 15.6501 38.1796 16.6L38.1796 22.8499C38.1413 23.0213 38.1402 23.1993 38.1764 23.3711C38.2126 23.543 38.2851 23.7044 38.3889 23.844C38.4927 23.9835 38.625 24.0976 38.7765 24.1781C38.9281 24.2586 39.0949 24.3035 39.2652 24.3096C39.3799 24.3055 39.4942 24.3249 39.6015 24.3667C39.7088 24.4085 39.8069 24.4719 39.8903 24.5532C39.9737 24.6345 40.0406 24.732 40.0872 24.8402C40.1338 24.9484 40.1591 25.065 40.1617 25.1834L40.1617 27.7738C40.1618 27.9589 40.105 28.1393 39.9994 28.289C39.8938 28.4386 39.7448 28.5498 39.574 28.6064C38.9061 28.8174 38.2107 28.9214 37.5122 28.9148C36.8091 28.9517 36.106 28.8414 35.4453 28.5905C34.7846 28.3397 34.1799 27.9535 33.6676 27.4551L33.6277 27.4551C32.1583 28.4789 30.4203 29.0134 28.6476 28.9868C27.1904 29.0217 25.7569 28.6025 24.5339 27.7841ZM32.6914 23.5181L32.6914 22.0481C32.6918 21.841 32.6209 21.6406 32.4914 21.4824C32.3619 21.3243 32.1822 21.2187 31.9843 21.1846C31.5723 21.1188 31.1561 21.0844 30.7392 21.0818C29.3249 21.0818 27.9105 21.5341 27.9105 22.9527C27.9105 24.1451 28.9065 24.7825 30.1914 24.7825C30.8818 24.7659 31.5609 24.5977 32.1835 24.289C32.3296 24.2237 32.4544 24.1162 32.5427 23.9794C32.6311 23.8426 32.6793 23.6824 32.6815 23.5181L32.6914 23.5181Z" fill="#3A3535"/><path d="M45.0029 9.81166L48.7082 9.81166C48.9327 9.81166 49.148 9.90372 49.3068 10.0676C49.4656 10.2314 49.5548 10.4537 49.5548 10.6854L49.5548 12.3199L49.6245 12.3199C50.1947 11.4506 50.9684 10.7442 51.8739 10.2662C52.7794 9.78816 53.7873 9.55401 54.8039 9.58551L55.1923 9.58551C55.4046 9.60361 55.6024 9.7036 55.7464 9.86554C55.8904 10.0275 55.97 10.2395 55.9692 10.4593L55.9692 14.0468C55.971 14.1674 55.9483 14.287 55.9026 14.398C55.8569 14.509 55.7892 14.6088 55.7038 14.6911C55.6184 14.7734 55.5172 14.8364 55.4069 14.9154C55.2965 14.9154 55.1793 14.9306 55.0628 14.9206L54.4851 14.9206C53.6302 14.8806 52.7769 15.0307 51.983 15.3605C51.1891 15.6904 50.4731 16.1924 49.8835 16.8326C49.7418 16.9926 49.6635 17.2017 49.6644 17.4185L49.6644 27.3485C49.6644 27.5802 49.5752 27.8025 49.4164 27.9663C49.2576 28.1302 49.0423 28.2222 48.8177 28.2222L45.0029 28.2222C44.7784 28.2222 44.5631 28.1302 44.4043 27.9663C44.2455 27.8025 44.1563 27.5802 44.1563 27.3485L44.1563 10.6957C44.155 10.5801 44.1759 10.4654 44.2179 10.3582C44.2598 10.251 44.322 10.1534 44.4007 10.0712C44.4795 9.98897 44.5733 9.9237 44.6766 9.87914C44.78 9.83459 44.8909 9.81165 45.0029 9.81166Z" fill="#3A3535"/><path d="M59.8701 5.04362e-06L63.6849 5.038e-06C63.9094 5.03767e-06 64.1247 0.0920646 64.2835 0.255926C64.4423 0.419787 64.5315 0.642026 64.5315 0.87376L64.5315 17.948L64.6012 17.948L70.5774 10.4542C70.6572 10.3541 70.7576 10.2736 70.8713 10.2185C70.985 10.1634 71.1092 10.135 71.2348 10.1356L75.2687 10.1356C75.4304 10.1352 75.5889 10.1826 75.7253 10.2723C75.8617 10.3619 75.9704 10.49 76.0385 10.6414C76.1066 10.7928 76.1313 10.9611 76.1096 11.1265C76.0879 11.2919 76.0208 11.4475 75.9161 11.5747L70.7368 17.8349C70.6172 17.9897 70.552 18.1818 70.552 18.3797C70.552 18.5777 70.6172 18.7698 70.7368 18.9246L76.6532 27.1482C76.7475 27.278 76.805 27.4323 76.8191 27.5939C76.8332 27.7556 76.8034 27.918 76.7331 28.0632C76.6628 28.2083 76.5547 28.3304 76.421 28.4157C76.2872 28.501 76.1331 28.5462 75.9759 28.5462L71.5236 28.5462C71.3888 28.5454 71.2561 28.5114 71.1365 28.4471C71.017 28.3827 70.9141 28.2898 70.8364 28.1761L64.6012 19.1507L64.5315 19.1507L64.5315 27.6313C64.5315 27.863 64.4423 28.0853 64.2835 28.2491C64.1247 28.413 63.9094 28.505 63.6849 28.505L59.8701 28.505C59.6455 28.505 59.4302 28.413 59.2714 28.2491C59.1126 28.0853 59.0234 27.863 59.0234 27.6313L59.0234 0.801804C59.041 0.582759 59.1379 0.378587 59.2948 0.229982C59.4517 0.0813776 59.6571 -0.000741433 59.8701 5.04362e-06Z" fill="#3A3535"/><path d="M82.4317 34.2606L85.8083 26.8799C85.8608 26.764 85.888 26.6377 85.888 26.5098C85.888 26.3819 85.8608 26.2556 85.8083 26.1398L79.2743 11.3681C79.2162 11.2354 79.1912 11.0898 79.2015 10.9446C79.2118 10.7993 79.2571 10.659 79.3333 10.5365C79.4095 10.4139 79.5142 10.3129 79.6378 10.2427C79.7615 10.1725 79.9002 10.1354 80.0413 10.1346L83.9955 10.1346C84.1615 10.1353 84.3237 10.1863 84.4618 10.2813C84.6 10.3764 84.708 10.5112 84.7724 10.6691L86.376 14.7295C87.1729 16.71 87.9332 18.6563 88.6569 20.5683L88.7267 20.5683C89.5235 18.5124 90.3203 16.6004 91.157 14.6267L92.8203 10.7C92.8863 10.544 92.995 10.4113 93.133 10.3181C93.271 10.225 93.4324 10.1755 93.5973 10.1757L97.2128 10.1757C97.354 10.1765 97.4926 10.2137 97.6163 10.2839C97.7399 10.354 97.8446 10.455 97.9208 10.5776C97.997 10.7001 98.0423 10.8404 98.0526 10.9857C98.0629 11.1309 98.0379 11.2765 97.9798 11.4092L87.7008 35.0521C87.634 35.2045 87.5262 35.334 87.3903 35.4251C87.2543 35.5162 87.0959 35.5651 86.9338 35.5661L83.1987 35.5661C83.048 35.5718 82.8985 35.5359 82.7657 35.462C82.6329 35.3881 82.5217 35.279 82.4435 35.1459C82.3653 35.0128 82.323 34.8605 82.3209 34.7049C82.3189 34.5492 82.3571 34.3958 82.4317 34.2606Z" fill="#3A3535"/><path d="M101.606 10.163L105.351 10.163C105.575 10.163 105.791 10.2551 105.949 10.4189C106.108 10.5828 106.197 10.805 106.197 11.0368L106.197 11.8488L106.267 11.8488C107.81 10.5038 109.756 9.75187 111.775 9.72097C112.636 9.69335 113.493 9.84476 114.295 10.1662C115.098 10.4876 115.829 10.9724 116.446 11.5918C117.532 12.7843 118 14.3981 118 17.0503L118 27.6587C118 27.8904 117.911 28.1127 117.752 28.2765C117.594 28.4404 117.378 28.5324 117.154 28.5324L113.339 28.5324C113.114 28.5324 112.899 28.4404 112.74 28.2765C112.581 28.1127 112.492 27.8904 112.492 27.6587L112.492 18.0165C112.608 17.1069 112.387 16.1857 111.875 15.4364C111.637 15.1699 111.345 14.9615 111.019 14.8264C110.693 14.6912 110.343 14.6327 109.992 14.6551C108.773 14.7136 107.6 15.1507 106.626 15.9092C106.513 15.9899 106.42 16.0974 106.356 16.2226C106.292 16.3479 106.258 16.4872 106.257 16.6288L106.257 27.6895C106.257 27.9213 106.168 28.1435 106.009 28.3074C105.85 28.4712 105.635 28.5633 105.41 28.5633L101.596 28.5633C101.371 28.5633 101.156 28.4712 100.997 28.3074C100.838 28.1435 100.749 27.9213 100.749 27.6895L100.749 11.0368C100.749 10.9211 100.771 10.8067 100.814 10.7C100.858 10.5933 100.921 10.4965 101.001 10.4153C101.08 10.334 101.175 10.2698 101.279 10.2265C101.382 10.1832 101.494 10.1616 101.606 10.163Z" fill="#3A3535"/></svg></span>
    <div class="lp-preloader__ring-container" id="lp-ring-container">
      <svg class="lp-preloader__ring-svg" id="lp-ring-svg" viewBox="0 0 200 200">
        <circle class="lp-preloader__ring-track" cx="100" cy="100" r="90" />
        <circle class="lp-preloader__ring-progress" id="lp-ring-progress" cx="100" cy="100" r="90" />
        <line class="lp-preloader__ring-tick" x1="100" y1="10" x2="100" y2="24" stroke="${phases[0].color}" transform="rotate(90, 100, 100)"/>
        <line class="lp-preloader__ring-tick" x1="100" y1="10" x2="100" y2="24" stroke="${phases[1].color}" transform="rotate(210, 100, 100)"/>
        <line class="lp-preloader__ring-tick" x1="100" y1="10" x2="100" y2="24" stroke="${phases[2].color}" transform="rotate(330, 100, 100)"/>
      </svg>
      <div class="lp-preloader__ring-center">
        <div class="lp-preloader__ring-icon" id="lp-ring-icon" style="background:${phases[0].iconBg}">
          ${phases[0].iconSvg}
        </div>
      </div>
    </div>
    <div class="lp-preloader__text-area">
      <div class="lp-preloader__phase-label" id="lp-phase-label-container">
        <span class="lp-preloader__phase-pip" id="lp-phase-pip" style="background:${phases[0].color}"></span>
        <span id="lp-phase-label-text">${phases[0].label}</span>
      </div>
      <div class="lp-preloader__phase-title" id="lp-phase-title">${phases[0].title}</div>
    </div>
    <div class="lp-preloader__progress-container" id="lp-preloader-progress">
      <div class="lp-preloader__progress-track">
        <div class="lp-preloader__progress-fill" id="lp-progress-fill" style="background:${phases[0].color}"></div>
      </div>
      <div class="lp-preloader__progress-labels">
        <span id="lp-progress-pct">0%</span>
        <span id="lp-progress-phase">${phases[0].phaseName}</span>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const ringProgress = document.getElementById('lp-ring-progress');
  const ringIcon = document.getElementById('lp-ring-icon');
  const phasePip = document.getElementById('lp-phase-pip');
  const phaseLabelText = document.getElementById('lp-phase-label-text');
  const phaseTitle = document.getElementById('lp-phase-title');
  const progressFill = document.getElementById('lp-progress-fill');
  const progressPct = document.getElementById('lp-progress-pct');
  const progressPhase = document.getElementById('lp-progress-phase');

  const phaseLabelContainer = document.getElementById('lp-phase-label-container');
  const progressContainer = document.getElementById('lp-preloader-progress');

  const circumference = 2 * Math.PI * 90;
  const TOTAL_DURATION = 3000;
  let startTime = null;
  let currentPhase = -1;

  function setPhase(index) {
    if (index === currentPhase) return;
    currentPhase = index;
    const p = phases[index];
    phaseTitle.style.opacity = '0';
    setTimeout(() => {
      phaseLabelText.textContent = p.label;
      phasePip.style.background = p.color;
      phaseTitle.innerHTML = p.title;
      progressPhase.textContent = p.phaseName;
      ringProgress.style.stroke = p.color;
      progressFill.style.background = p.color;
      ringIcon.style.background = p.iconBg;
      ringIcon.innerHTML = p.iconSvg;
      phaseTitle.style.opacity = '1';
    }, 250);
  }

  function tick(ts) {
    if (!startTime) startTime = ts;
    const progress = Math.min((ts - startTime) / TOTAL_DURATION, 1);
    ringProgress.style.strokeDashoffset = circumference * (1 - progress);
    const pct = Math.round(progress * 100);
    progressFill.style.width = pct + '%';
    progressPct.textContent = pct + '%';
    setPhase(Math.min(Math.floor(progress * 3), 2));
    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      // Success state
      phaseLabelContainer.style.opacity = '0';
      progressContainer.style.opacity = '0';
      document.getElementById('lp-ring-svg').style.display = 'none';
      const ringContainer = document.getElementById('lp-ring-container');
      ringContainer.style.width = '72px';
      ringContainer.style.height = '72px';
      const ringCenter = ringContainer.querySelector('.lp-preloader__ring-center');
      ringCenter.style.position = 'static';
      ringCenter.style.display = 'flex';
      ringCenter.style.alignItems = 'center';
      ringCenter.style.justifyContent = 'center';
      ringIcon.style.background = '#7BA87C';
      ringIcon.style.width = '72px';
      ringIcon.style.height = '72px';
      ringIcon.style.borderRadius = '50%';
      ringIcon.innerHTML = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;
      overlay.style.gap = '32px';
      phaseTitle.innerHTML = allergyBlocked ? 'Está tudo registado!' : 'Tudo pronto!';

      setTimeout(() => {
        if (allergyBlocked) {
          overlay.remove();
          showChickenBlock();
          return;
        }
        window.location.href = '/pages/longevity-plan-quiz-results';
      }, 500);
    }
  }

  requestAnimationFrame(tick);
}

function buildFoodTypeChoice(step, num) {
  const saved = state.answers[step.key];
  const images = window.__lpqFoodTypeImages || {};

  const cards = step.options.map(opt => {
    const isSelected = saved === opt.value;
    const imgUrl = images[opt.imageKey] || '';
    return `
      <button
        class="lp-quiz__food-card${isSelected ? ' lp-quiz__food-card--selected' : ''}"
        data-value="${escapeHtml(opt.value)}"
        type="button"
      >
        <div class="lp-quiz__food-card__body">
          <span class="lp-quiz__food-card__label">${opt.label}</span>
          ${opt.subtitle ? `<span class="lp-quiz__food-card__subtitle">${opt.subtitle}</span>` : ''}
        </div>
        ${imgUrl ? `<img class="lp-quiz__food-card__img" src="${imgUrl}" alt="${escapeHtml(opt.label)}" loading="eager">` : ''}
      </button>
    `;
  }).join('');

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${interpolate(step.question)}</h1>
          <div class="lp-quiz__food-cards">${cards}</div>
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button class="lp-quiz__next-btn" id="lp-next" type="button" ${saved !== undefined ? '' : 'disabled'}>Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initFoodTypeChoice(step) {
  const next = document.getElementById('lp-next');

  syncFooterSticky(!next.disabled);
  document.getElementById('lp-back').addEventListener('click', goBack);

  document.querySelectorAll('.lp-quiz__food-card').forEach(card => {
    card.addEventListener('click', () => {
      document.querySelectorAll('.lp-quiz__food-card').forEach(c => c.classList.remove('lp-quiz__food-card--selected'));
      card.classList.add('lp-quiz__food-card--selected');
      state.answers[step.key] = card.dataset.value;
      storage.save(state.answers);
      next.disabled = false;
      syncFooterSticky(true);
      setTimeout(advance, 300);
    });
  });

  next.addEventListener('click', () => {
    if (!next.disabled) advance();
  });
}

function buildSingleChoice(step, num) {
  const saved = state.answers[step.key];
  const gridClass = step.columns === 3
    ? 'lp-quiz__choice-grid lp-quiz__choice-grid--3'
    : 'lp-quiz__choice-grid';

  const btns = step.options.map(opt => {
    const isSelected = saved === opt.value;
    return `
      <button
        class="lp-quiz__choice-btn${isSelected ? ' lp-quiz__choice-btn--selected' : ''}${opt.subtitle ? ' lp-quiz__choice-btn--with-subtitle' : ''}"
        data-value="${escapeHtml(opt.value)}"
        type="button"
      >
        <span class="lp-quiz__choice-label">${opt.label}</span>
        ${opt.subtitle ? `<span class="lp-quiz__choice-subtitle">${opt.subtitle}</span>` : ''}
      </button>
    `;
  }).join('');

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${interpolate(step.question)}</h1>
          <div class="${gridClass}">${btns}</div>
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button class="lp-quiz__next-btn" id="lp-next" type="button" ${saved !== undefined ? '' : 'disabled'}>Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initSingleChoice(step) {
  const next = document.getElementById('lp-next');

  syncFooterSticky(!next.disabled);

  document.getElementById('lp-back').addEventListener('click', goBack);

  document.querySelectorAll('.lp-quiz__choice-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.lp-quiz__choice-btn').forEach(b => b.classList.remove('lp-quiz__choice-btn--selected'));
      btn.classList.add('lp-quiz__choice-btn--selected');
      state.answers[step.key] = btn.dataset.value;
      storage.save(state.answers);
      next.disabled = false;
      syncFooterSticky(true);
      setTimeout(advance, 300);
    });
  });

  next.addEventListener('click', () => {
    if (!next.disabled) advance();
  });
}

/**
 * Birthdate step, puppies only.
 *
 * Month + year, not a full date — owners rarely know the exact day and nothing
 * uses it. Stored as YYYY-MM-01, which fits the dogs API `birthdate` format.
 *
 * The year list is only this year and last year, since the dog is under one, and
 * future months are filtered out so the date can never be invalid. `age` is left
 * exactly as the owner answered it — the bucket is what drives the plan, and
 * silently reclassifying a dog mid-quiz would change which steps they see.
 */
function buildBirthdateInput(step, num) {
  const saved = state.answers[step.key] || '';
  const [savedYear, savedMonth] = saved ? saved.split('-') : ['', ''];

  const now = new Date();
  // A puppy is under a year, so this year and last cover it — plus one more
  // year of slack for owners who picked "puppy" loosely or aren't sure.
  const years = [0, 1, 2].map(offset => now.getFullYear() - offset);

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${interpolate(step.question)}</h1>

          <div class="lp-quiz__birthdate">
            <select class="lp-quiz__birthdate-select" id="lp-birthdate-month" aria-label="Mês">
              <option value="" disabled${savedMonth ? '' : ' selected'}>Mês</option>
            </select>
            <select class="lp-quiz__birthdate-select" id="lp-birthdate-year" aria-label="Ano">
              <option value="" disabled${savedYear ? '' : ' selected'}>Ano</option>
              ${years.map(year =>
                `<option value="${year}"${String(year) === savedYear ? ' selected' : ''}>${year}</option>`,
              ).join('')}
            </select>
          </div>

          <button class="lp-quiz__birthdate-unknown" id="lp-birthdate-skip" type="button">
            ${step.skipLabel}
          </button>

          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${interpolate(step.fact)}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button class="lp-quiz__next-btn" id="lp-next" type="button" ${saved ? '' : 'disabled'}>Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initBirthdateInput(step) {
  const monthSelect = document.getElementById('lp-birthdate-month');
  const yearSelect = document.getElementById('lp-birthdate-year');
  const skipBtn = document.getElementById('lp-birthdate-skip');
  const next = document.getElementById('lp-next');

  const saved = state.answers[step.key] || '';
  const [, savedMonth] = saved ? saved.split('-') : ['', ''];
  const now = new Date();

  // A dog born this year can't have been born in a month that hasn't happened.
  // `desired` is passed on the first render because the month options don't
  // exist yet at that point — reading monthSelect.value would come back empty
  // and drop the saved month when coming back to this step.
  const renderMonths = (desired) => {
    const keep = desired === undefined ? monthSelect.value : desired;
    const isThisYear = Number(yearSelect.value) === now.getFullYear();
    const lastMonth = isThisYear ? now.getMonth() + 1 : 12;

    monthSelect.innerHTML =
      `<option value="" disabled${keep ? '' : ' selected'}>Mês</option>` +
      MONTH_LABELS.slice(0, lastMonth).map((label, index) => {
        const value = String(index + 1).padStart(2, '0');
        return `<option value="${value}"${value === keep ? ' selected' : ''}>${label}</option>`;
      }).join('');

    // The kept month may not exist in the new list.
    if (keep && monthSelect.value !== keep) monthSelect.value = '';
  };

  renderMonths(savedMonth);
  syncFooterSticky(!next.disabled);

  document.getElementById('lp-back').addEventListener('click', goBack);

  const setEnabled = (enabled) => {
    next.disabled = !enabled;
    syncFooterSticky(enabled);
  };

  const commit = () => {
    const month = monthSelect.value;
    const year = yearSelect.value;
    if (!month || !year) return setEnabled(false);

    state.answers[step.key] = `${year}-${month}-01`;
    state.answers.isBirthdateUnknown = false;
    storage.save(state.answers);
    setEnabled(true);
  };

  yearSelect.addEventListener('change', () => {
    renderMonths();
    commit();
  });
  monthSelect.addEventListener('change', commit);

  skipBtn.addEventListener('click', () => {
    delete state.answers[step.key];
    state.answers.isBirthdateUnknown = true;
    storage.save(state.answers);
    advance();
  });

  next.addEventListener('click', () => {
    if (!next.disabled) advance();
  });
}

function goBack() {
  const previous = stepFrom(state.step, -1);
  if (previous === undefined) return;
  state.step = previous;
  renderStep(state.step);
}

function updateFooterShadow() {
  const footer = document.getElementById('lp-footer');
  if (!footer || !footer.classList.contains('lp-quiz__footer--sticky')) return;
  const hasScroll = document.documentElement.scrollHeight > window.innerHeight + 80;
  const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
  footer.classList.toggle('lp-quiz__footer--shadowed', hasScroll && !atBottom);
}

function updateFooterKeyboardOffset() {
  const footer = document.getElementById('lp-footer');
  if (!footer || !footer.classList.contains('lp-quiz__footer--sticky')) return;
  if (!window.visualViewport) return;
  const vv = window.visualViewport;
  const keyboardHeight = window.innerHeight - (vv.offsetTop + vv.height);
  footer.style.bottom = keyboardHeight > 0 ? keyboardHeight + 'px' : '';
}

function syncFooterSticky(isEnabled) {
  const footer = document.getElementById('lp-footer');
  if (!footer) return;
  const onMobile = window.matchMedia('(max-width: 768px)').matches;
  footer.classList.toggle('lp-quiz__footer--sticky', isEnabled || onMobile);
  updateFooterShadow();
  updateFooterKeyboardOffset();
}

function buildChoiceGrid(group) {
  const saved = state.answers[group.key];
  const btns = group.options.map(opt => `
    <button
      class="lp-quiz__choice-btn${saved === opt.value ? ' lp-quiz__choice-btn--selected' : ''}"
      data-value="${opt.value}"
      type="button"
    >${opt.label}</button>
  `).join('');
  return `<div class="lp-quiz__choice-grid" data-key="${group.key}">${btns}</div>`;
}

function buildSingleChoiceGroup(step, num) {
  const allAnswered = step.groups.every(g => state.answers[g.key] !== undefined);
  const genderAnswered = state.answers[step.groups[0].key] !== undefined;

  const groupsHtml = step.groups.map((g, i) => {
    const isSecondary = i > 0;
    const hidden = isSecondary && !genderAnswered;

    let questionHtml;
    if (i === 0) {
      questionHtml = `<h1 class="lp-quiz__question">${interpolate(g.question)}</h1>`;
    } else {
      const gender = state.answers[step.groups[0].key];
      const qText = gender === 'female' ? g.question_female : g.question_male;
      questionHtml = `<h2 class="lp-quiz__sub-question"
        data-question-male="${escapeHtml(g.question_male || '')}"
        data-question-female="${escapeHtml(g.question_female || '')}"
      >${interpolate(qText || g.question_male || '')}</h2>`;
    }

    return `
      <div class="lp-quiz__sub-group${hidden ? ' lp-quiz__sub-group--hidden' : ''}" data-group-index="${i}">
        ${questionHtml}
        ${buildChoiceGrid(g)}
      </div>
    `;
  }).join('');

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          ${groupsHtml}
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button class="lp-quiz__next-btn" id="lp-next" type="button" ${allAnswered ? '' : 'disabled'}>Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initSingleChoiceGroup(step) {
  const next = document.getElementById('lp-next');

  syncFooterSticky(!next.disabled);

  document.getElementById('lp-back').addEventListener('click', goBack);

  document.querySelectorAll('.lp-quiz__choice-grid').forEach(grid => {
    const key = grid.dataset.key;
    const isGenderGrid = key === step.groups[0].key;

    grid.querySelectorAll('.lp-quiz__choice-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        grid.querySelectorAll('.lp-quiz__choice-btn').forEach(b => b.classList.remove('lp-quiz__choice-btn--selected'));
        btn.classList.add('lp-quiz__choice-btn--selected');
        state.answers[key] = btn.dataset.value;
        storage.save(state.answers);

        if (isGenderGrid) {
          const secondGroup = document.querySelector('.lp-quiz__sub-group[data-group-index="1"]');
          if (secondGroup) {
            const subQ = secondGroup.querySelector('.lp-quiz__sub-question');
            if (subQ) {
              const qAttr = btn.dataset.value === 'female' ? 'data-question-female' : 'data-question-male';
              subQ.textContent = interpolate(subQ.getAttribute(qAttr));
            }
            secondGroup.classList.remove('lp-quiz__sub-group--hidden');
          }
        }

        next.disabled = !step.groups.every(g => state.answers[g.key] !== undefined);
        syncFooterSticky(!next.disabled);
        if (!next.disabled) setTimeout(advance, 300);
      });
    });
  });

  next.addEventListener('click', () => {
    if (!next.disabled) advance();
  });
}

const backArrowSvg = `<svg class="lp-quiz__back-icon" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#lp-back-clip)"><path d="M27.0024 15.9989L5.00244 15.9989" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.001 24.9979L5.00098 15.9979L14.001 6.99788" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="lp-back-clip"><rect width="32" height="32" fill="white" transform="translate(32 32) rotate(-180)"/></clipPath></defs></svg>`;
const tickSvg = `<svg class="lp-quiz__tick" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>`;
const lockSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>`;
const checkSvg = `<svg class="lp-quiz__check-icon" width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.46875 4.6875L3.75 7.96875L11.25 0.46875" stroke="white" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// A blocking allergy no longer stops the quiz here — it is handled after the email, in
// showChickenBlock — so this step behaves like any other multi-choice.
function buildMultiChoice(step, num) {
  const saved = state.answers[step.key] || [];
  const btns = step.options.map(opt => `
    <button
      class="lp-quiz__choice-btn lp-quiz__choice-btn--multi${saved.includes(opt.value) ? ' lp-quiz__choice-btn--selected' : ''}"
      data-value="${opt.value}"
      type="button"
    ><span class="lp-quiz__choice-check">${checkSvg}</span>${opt.label}</button>
  `).join('');

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${interpolate(step.question)}</h1>
          <div class="lp-quiz__choice-grid lp-quiz__choice-grid--3">${btns}</div>
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button class="lp-quiz__next-btn" id="lp-next" type="button">Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initMultiChoice(step) {
  syncFooterSticky(true);

  document.getElementById('lp-back').addEventListener('click', goBack);

  document.querySelectorAll('.lp-quiz__choice-btn--multi').forEach(btn => {
    btn.addEventListener('click', () => {
      const val = btn.dataset.value;
      const allBtns = document.querySelectorAll('.lp-quiz__choice-btn--multi');
      if (val === 'none') {
        allBtns.forEach(b => b.classList.remove('lp-quiz__choice-btn--selected'));
        btn.classList.add('lp-quiz__choice-btn--selected');
      } else {
        document.querySelector('.lp-quiz__choice-btn--multi[data-value="none"]')
          ?.classList.remove('lp-quiz__choice-btn--selected');
        btn.classList.toggle('lp-quiz__choice-btn--selected');
      }
      state.answers[step.key] = [...allBtns]
        .filter(b => b.classList.contains('lp-quiz__choice-btn--selected'))
        .map(b => b.dataset.value);
      storage.save(state.answers);
      // "Nenhuma" is a complete answer on its own — nothing else can be picked with it, so it
      // behaves like a single choice and moves on. Same 300ms as the single-choice steps, long
      // enough to see the tick land.
      if (val === 'none') setTimeout(advance, 300);
    });
  });

  document.getElementById('lp-next').addEventListener('click', advance);
}

function buildNumberInput(step, num) {
  const saved = escapeHtml(state.answers[step.key] || '');
  const hasValue = saved.length > 0;
  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          <h1 class="lp-quiz__question">${interpolate(step.question)}</h1>
          <div class="lp-quiz__input-wrapper">
            <input
              type="text"
              inputmode="decimal"
              class="lp-quiz__text-input lp-quiz__text-input--with-unit"
              id="lp-input"
              placeholder="${step.placeholder}"
              value="${saved}"
              autocomplete="off"
            >
            <span class="lp-quiz__input-unit">${step.unit}</span>
          </div>
          <p class="lp-quiz__input-warning" id="lp-input-warning">Só números, por favor</p>
          <div class="lp-quiz__bottom">
            <p class="lp-quiz__fact">${step.fact}</p>
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button
                class="lp-quiz__next-btn"
                id="lp-next"
                type="button"
                ${hasValue ? '' : 'disabled'}
              >Avançar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initNumberInput(step) {
  const input = document.getElementById('lp-input');
  const next = document.getElementById('lp-next');
  const warning = document.getElementById('lp-input-warning');

  syncFooterSticky(true);

  document.getElementById('lp-back').addEventListener('click', goBack);

  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const val = input.value.trim();
      if (val && !isNaN(parseFloat(val))) {
        state.answers[step.key] = val;
        storage.save(state.answers);
        advance();
      }
      return;
    }
    if (allowedKeys.includes(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) return;
    if (/^[0-9]$/.test(e.key)) return;
    if ((e.key === '.' || e.key === ',') && !input.value.includes('.') && !input.value.includes(',')) return;
    e.preventDefault();
    warning.classList.add('lp-quiz__input-warning--visible');
  });

  input.addEventListener('input', () => {
    const val = parseFloat(input.value.trim());
    const isValid = !isNaN(val) && val > 0;
    next.disabled = !isValid;
    if (input.value.trim().length > 0) warning.classList.remove('lp-quiz__input-warning--visible');
  });

  next.addEventListener('click', () => {
    const val = input.value.trim();
    if (!val || isNaN(parseFloat(val))) return;
    state.answers[step.key] = val;
    storage.save(state.answers);
    advance();
  });

  input.focus();
}

function isValidEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

/**
 * The one step that has to earn something: the email buys the plan. So it states what is behind
 * the email (heading + lede), shows it locked (a blurred stand-in with a padlock), and lists what
 * comes with it — instead of just asking for an address.
 */
function buildEmailInput(step, num) {
  const saved = escapeHtml(state.answers[step.key] || '');
  const isValid = isValidEmail(saved);

  const trust = (step.trust || [])
    .map(text => `<li class="lp-quiz__trust-item">${tickSvg}<span>${interpolate(text)}</span></li>`)
    .join('');

  // aria-hidden: the rows are a stand-in, not data. The heading and the lede carry the same
  // message in text, so nothing is lost with a screen reader.
  const locked = step.lockedRows
    ? `
      <div class="lp-quiz__locked" aria-hidden="true">
        <div class="lp-quiz__locked-sheet">
          ${step.lockedRows.map(row => `
            <div class="lp-quiz__locked-row">
              <span>${row.label}</span>
              <span class="lp-quiz__locked-value">${row.value}</span>
            </div>`).join('')}
        </div>
        <div class="lp-quiz__locked-veil">
          <span class="lp-quiz__locked-lock">${lockSvg}</span>
          <span class="lp-quiz__locked-title">${interpolate(step.lockedTitle || '')}</span>
        </div>
      </div>`
    : '';

  return `
    <div class="lp-quiz__step" data-step="${num}">
      <div class="lp-quiz__step-cols">
        <div class="lp-quiz__step-col-left">
          <div class="lp-quiz__counter">
            <strong>${displayStep(num)}</strong><span class="lp-quiz__counter-total">/${totalSteps()}</span>
          </div>
        </div>
        <div class="lp-quiz__step-col-right">
          ${locked}
          <h1 class="lp-quiz__question${step.lede ? ' lp-quiz__question--email' : ''}">${interpolate(step.question)}</h1>
          ${step.lede ? `<p class="lp-quiz__lede">${interpolate(step.lede)}</p>` : ''}
          <div class="lp-quiz__field">
            ${step.fieldLabel ? `<label class="lp-quiz__field-label" for="lp-input">${step.fieldLabel}</label>` : ''}
            <input
              type="email"
              class="lp-quiz__text-input"
              id="lp-input"
              placeholder="${step.placeholder}"
              value="${saved}"
              autocomplete="email"
            >
            <p class="lp-quiz__input-warning" id="lp-input-warning">Introduz um email válido</p>
          </div>
          ${trust ? `<ul class="lp-quiz__trust">${trust}</ul>` : ''}
          <div class="lp-quiz__bottom">
            ${step.fact ? `<p class="lp-quiz__fact">${interpolate(step.fact)}</p>` : ''}
            <div class="lp-quiz__footer" id="lp-footer">
              <button class="lp-quiz__back-btn" id="lp-back" type="button">${backArrowSvg}</button>
              <button
                class="lp-quiz__next-btn"
                id="lp-next"
                type="button"
                ${isValid ? '' : 'disabled'}
              >${step.ctaLabel ? interpolate(step.ctaLabel) : 'Avançar'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function initEmailInput(step) {
  const input = document.getElementById('lp-input');
  const next = document.getElementById('lp-next');
  const warning = document.getElementById('lp-input-warning');

  syncFooterSticky(!next.disabled);

  document.getElementById('lp-back').addEventListener('click', goBack);

  input.addEventListener('input', () => {
    const valid = isValidEmail(input.value);
    next.disabled = !valid;
    syncFooterSticky(valid);
    if (valid) warning.classList.remove('lp-quiz__input-warning--visible');
  });

  input.addEventListener('blur', () => {
    if (input.value.trim() && !isValidEmail(input.value)) {
      warning.classList.add('lp-quiz__input-warning--visible');
    }
  });

  const submit = () => {
    if (!isValidEmail(input.value)) {
      warning.classList.add('lp-quiz__input-warning--visible');
      return;
    }
    // Enter fires this and so does the click that follows it; re-rendering the step re-enables it.
    next.disabled = true;
    state.answers[step.key] = input.value.trim();
    storage.save(state.answers);
    advance();
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });

  next.addEventListener('click', submit);

  input.focus();
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

document.addEventListener('DOMContentLoaded', () => {
  const params = new URLSearchParams(location.search);

  const urlDiscount = params.get('discount');
  if (urlDiscount && discountPercentOf(urlDiscount) > 0) {
    const incoming = urlDiscount.toUpperCase();
    const existing = (localStorage.getItem('longevityDiscountCode') || '');
    const incomingPct = discountPercentOf(incoming);
    const existingPct = discountPercentOf(existing);
    const code = incomingPct >= existingPct ? incoming : existing;
    if (incomingPct >= existingPct) localStorage.setItem('longevityDiscountCode', incoming);
    state.answers.discountCode = code;
    if (state.answers.isCompleted) {
      storage.complete(state.answers);
    } else {
      storage.save(state.answers);
    }
  }

  const urlDogName = (params.get('dog_name') || '').trim().slice(0, 50);
  if (urlDogName) {
    state.answers.name = urlDogName;
    storage.save(state.answers);
    if (state.step < 2) state.step = 2;
  }

  const urlFood = params.get('food');
  if (urlFood === 'fresh' || urlFood === 'kibble') {
    forcedFood = urlFood;
    state.answers.foodType = urlFood;
    storage.save(state.answers);
    if (state.step === 3) state.step = 4;
  }

  renderStep(state.step);
  window.addEventListener('scroll', updateFooterShadow, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateFooterKeyboardOffset, { passive: true });
    window.visualViewport.addEventListener('scroll', updateFooterKeyboardOffset, { passive: true });
  }
});

window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  document.querySelector('.lp-preloader')?.remove();
  state.step = 1;
  renderStep(1);
});
